/**
 * Searching the food database (TACO, then Open Food Facts), shared by the calorie tracker's add-food sheet and the
 * diet plan's food editor. Results come per 100 g.
 */
import { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';

import { SEARCH_FOODS } from '../graphql/queries';

/** Open Food Facts results have no id. */
export const foodKey = (food) => (food.id != null ? `id-${food.id}` : `${food.source || 'off'}-${food.name_pt}`);

/** The food's name in the site's language (TACO foods have only the Portuguese one). */
export const searchedFoodName = (food, language) => (language === 'pt' ? food.name_pt : food.name_en) || food.name_pt;

/** The foods matching `query`, searched 400 ms after the typing stops. */
export const useFoodSearch = (query, language) => {
  const [results, setResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const { refetch: searchFoods } = useQuery(SEARCH_FOODS, { variables: { query: '', lang: language }, skip: true });

  useEffect(() => {
    const term = query.trim();
    if (!term) {
      setResults([]);
      setHasSearched(false);
      setIsSearching(false);
      return undefined;
    }
    setIsSearching(true);
    const timer = setTimeout(async () => {
      try {
        const response = await searchFoods({ query: term, lang: language });
        // Open Food Facts entries without calories can't be used
        setResults((response?.data?.searchFoods || []).filter((food) => food.calories != null));
      } catch {
        setResults([]);
      }
      setHasSearched(true);
      setIsSearching(false);
    }, 400);
    return () => clearTimeout(timer);
  }, [query, language, searchFoods]);

  return { results, isSearching, hasSearched };
};
